import React from "react";
import { useResumeStore } from "../../store/resumeStore";
import {
  getAboutText,
  getLanguages,
  getProjects,
  stripHtml,
} from "./resumeTemplateUtils";

const MISSING_SECTIONS_BY_TEMPLATE = {
  template7: ["summary", "projects", "awards", "publications", "customSections"],
  template12: ["summary", "projects", "awards", "publications", "customSections"],
  template13: [
    "projects",
    "languages",
    "achievements",
    "awards",
    "publications",
    "customSections",
  ],
  template15: ["summary", "projects", "awards", "publications", "customSections"],
  template21: ["projects", "languages", "certifications", "awards", "publications", "customSections"],
  template22: ["projects", "languages", "certifications", "awards", "publications", "customSections"],
  template23: ["projects", "contact", "certifications", "awards", "publications", "customSections"],
  template24: ["experience", "projects", "certifications", "awards", "publications", "customSections"],
  template25: ["experience", "projects", "languages", "certifications", "awards", "publications", "customSections"],
  template26: ["projects", "languages", "certifications", "awards", "publications", "customSections"],
  template27: ["projects", "languages", "certifications", "awards", "publications", "customSections"],
  template28: ["projects", "languages", "certifications", "awards", "publications", "customSections"],
  template29: ["projects", "languages", "certifications", "awards", "publications", "customSections"],
  template30: ["projects", "languages", "certifications", "awards", "publications", "customSections"],
  template31: ["projects", "contact", "certifications", "awards", "publications", "customSections"],
  template32: ["projects", "languages", "certifications", "awards", "publications", "customSections"],
  template33: ["projects", "languages", "certifications", "awards", "publications", "customSections"],
  template34: ["projects", "languages", "awards", "publications", "customSections"],
};

function textList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.flatMap(textList).filter(Boolean);
  }
  if (typeof value === "object") {
    const title = value.title || value.name || value.label || "";
    const items = textList(
      value.items ||
        value.entries ||
        value.content ||
        value.description ||
        value.value,
    );
    if (items.length) {
      return items.map((item) => (title ? `${title}: ${item}` : item));
    }
    return title ? [title] : [];
  }
  const text = stripHtml(String(value)).trim();
  return text ? [text] : [];
}

function Section({ title, children }) {
  if (!children) return null;
  return (
    <section
      data-section={title.toLowerCase().replace(/\s+/g, "-")}
      style={{ marginBottom: 14, breakInside: "avoid" }}
    >
      <h2
        style={{
          margin: "0 0 7px",
          paddingBottom: 4,
          borderBottom: "1px solid #cbd5e1",
          color: "#0f172a",
          fontFamily: "Arial, Helvetica, sans-serif",
          fontSize: 14,
          letterSpacing: 0,
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function List({ items }) {
  if (!items.length) return null;
  return (
    <ul
      style={{
        margin: 0,
        paddingLeft: 18,
        color: "#334155",
        fontFamily: "Arial, Helvetica, sans-serif",
        fontSize: 12,
        lineHeight: 1.45,
      }}
    >
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  );
}

export default function LegacyTemplateCompleteness({ templateId }) {
  const resume = useResumeStore((state) => state.resume);
  const missingSections = MISSING_SECTIONS_BY_TEMPLATE[templateId];
  if (!missingSections) return null;

  const projects = getProjects(resume).map((project) =>
    [project.title, project.subtitle].filter(Boolean).join(" — "),
  );
  const sections = {
    contact: {
      title: "Contact",
      items: [resume.location, resume.phone, resume.email].filter(Boolean),
    },
    experience: {
      title: "Experience",
      items: Array.isArray(resume.experience)
        ? resume.experience.map((item) =>
            [item.role, item.company, item.startDate, item.endDate]
              .filter(Boolean)
              .join(" · "),
          )
        : [],
    },
    summary: {
      title: "Summary",
      content: getAboutText(resume),
    },
    projects: {
      title: "Projects",
      items: projects,
    },
    languages: {
      title: "Languages",
      items: getLanguages(resume),
    },
    achievements: {
      title: "Achievements",
      items: textList(resume.achievements),
    },
    certifications: {
      title: "Certifications",
      items: textList(resume.certifications),
    },
    awards: {
      title: "Awards",
      items: textList(resume.awards),
    },
    publications: {
      title: "Publications",
      items: textList(resume.publications),
    },
    customSections: {
      title: "Additional Information",
      items: textList(resume.customSections),
    },
  };

  const populatedSections = missingSections
    .map((key) => sections[key])
    .filter((section) => section?.content || section?.items?.length);

  if (!populatedSections.length) return null;

  return (
    <div
      data-template-supplement={templateId}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "16px 20px 20px",
        background: "#ffffff",
        color: "#0f172a",
      }}
    >
      {populatedSections.map((section) => (
        <Section key={section.title} title={section.title}>
          {section.content ? (
            <p
              style={{
                margin: 0,
                color: "#334155",
                fontFamily: "Arial, Helvetica, sans-serif",
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              {section.content}
            </p>
          ) : (
            <List items={section.items} />
          )}
        </Section>
      ))}
    </div>
  );
}
